import { BLOCKLIST_MAX_ENTRIES } from './constants.js';
import type { Logger } from './log.js';

/**
 * Emergency creative takedown, tier 2 (design-addendum §2). The player checks the
 * blocklist before every item and skips silently — no gap on screen, no error, no
 * wait for a recompile. Two sources: `settings.blocklist` in the manifest and the
 * fleet-wide MQTT topic `dw/fleet/blocklist`, which reaches every device in one
 * publish. The MQTT subscription itself belongs to dwf-030; this takes updates from
 * whatever transport delivers them.
 */

export interface Blocklist {
  isBlocked(creativeId: string): boolean;
  updateFromManifest(entries: readonly string[] | undefined): void;
  updateFromFleet(entries: readonly string[]): void;
  snapshot(): readonly string[];
}

export function createBlocklist(logger: Logger): Blocklist {
  let fromManifest: readonly string[] = [];
  let fromFleet: readonly string[] = [];
  let merged = new Set<string>();

  const remerge = (): void => {
    merged = new Set([...fromManifest, ...fromFleet]);
  };

  const cap = (entries: readonly string[], source: string): readonly string[] => {
    if (entries.length <= BLOCKLIST_MAX_ENTRIES) return entries;
    logger.warn('blocklist.truncated', {
      source,
      received: entries.length,
      cap: BLOCKLIST_MAX_ENTRIES,
    });
    return entries.slice(0, BLOCKLIST_MAX_ENTRIES);
  };

  return {
    isBlocked: (creativeId) => merged.has(creativeId),
    updateFromManifest: (entries) => {
      fromManifest = cap(entries ?? [], 'manifest');
      remerge();
    },
    updateFromFleet: (entries) => {
      fromFleet = cap(entries, 'fleet_topic');
      remerge();
      logger.info('blocklist.fleet_updated', { entries: fromFleet.length });
    },
    snapshot: () => [...merged],
  };
}
